import { type DragEndEvent, type DragStartEvent, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import type { DateTime } from "luxon";
import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useGlobalManualLocationOrder } from "../../contexts/Staffing/GlobalManualLocationOrderContext";
import { useWeekViewContext } from "../../contexts/WeekView";
import { findLocationsWithNoNurse, type TimeSlot } from "../../downloads/dailyPrintout";
import { useContractSectionsFilter } from "../../hooks/useContractSectionsFilter";
import { useExpandedGroups } from "../../hooks/useExpandedGroups";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { dailyViewGapsTableSortingKey, localStorageService } from "../../services/LocalStorageService";
import { trackErrorEvent, trackEvent } from "../../utils/analytics";
import { ALLOW_AVAILABILITY_FALLBACK, byDimensionsOf, resolveLocationCensus, scoreAdjustedRequiredForLocation } from "../../utils/censusResolution";
import { adjustNightShiftRange, parseTimeString, roundToNearestHour } from "../../utils/dateUtils";
import { demoRequiredForDay } from "../../utils/demoRequiredStaff";
import {
	type DepartmentLocation,
	NotificationType,
	NurseFullRole,
	NurseFullRoleShort,
	type NurseInShift,
	type NurseRole,
	type OpenShiftRequest,
	type ReplaceShiftRequest,
} from "../../utils/models";
import { handleColumnSortClick, multiSortComparator, type SortingState } from "../../utils/multiColumnSorting";
import { getCensus, rolesMap } from "../../utils/utilFunctions";
import { useUpdateLocationAndHoursForShift } from "../NormalizedNurseMenu/useUpdateLocationAndHours";
import { useRequestUtils } from "../RequestData/Provider/useRequestUtils";
import { type LocationFilters, useCensusGrouping } from "../StaffTable/useCensusGrouping";
import { type ExactShiftFilterType, isSelectedTimeFrameValidForShiftFilter, ShiftFilterType } from "../staffing/MonthlyView/utils";
import { createPostedRequestMatcher } from "../staffing/WeeklyGapView/usePostedRequestMatcher";
import { matchesContractSections } from "../team/config";
import type { SortingButtonHandle } from "../UIToolkit/Popups/SystemSortingButtonWithPopup/SystemSortingButtonWithPopup";
import type { DraggableShiftData } from "./DailyRow/DraggableShiftBlock";
import type { DroppableGapData } from "./DailyRow/DroppableGapBlock";
import type { DailyGapsQuickAction } from "./DailyRow/NurseInShiftTrailingNode/utils";
import type { DayViewGapsProps } from "./DayViewGaps";
import {
	type CalculatedDayViewGaps,
	type CalculatedDayViewPostedRequests,
	computeGapPostedCoverageBySignature,
	type DayViewRowSlot,
	defaultSortStateDailyGapView,
	emptyLocationKey,
	filterNoLocationSlotsByJob,
	getDayViewGapRowKey,
	getRowNameAndRolesFromKey,
	noLocationString,
	normalizeStartEndWithStartOfDay,
	postedShiftString,
	type RowNameAndRoles,
	replaceRequestString,
	startAndEndTimeIncComparator,
	upsertPostedRequestIntoDayViewPostedRequests,
	upsertSlotIntoDayViewRows,
} from "./utils";

export type OptimisticMoveState = {
	sourceShiftId: string;
	sourceNurseId: number;
	sourceNurseInShift: NurseInShift;
	targetLocationKey: string;
	targetLocationName: string;
	targetStart: DateTime;
	targetEnd: DateTime;
};

export type GapPostShiftState = {
	locationName: string;
	startTime: DateTime;
	endTime: DateTime;
	roles: NurseFullRole[];
	dayNo: number;
	/** Job of the clicked gap (JobManagement only) — pre-selects the job in the Post Shift dialog. */
	jobId: string | null;
};

// Densest slots (most shifts/gaps) first; ties broken by start/end time so rows render top-to-bottom consistently.
const compareRowSlotsByDensity = (a: DayViewRowSlot, b: DayViewRowSlot): number =>
	a.shiftsAndGaps.length === b.shiftsAndGaps.length ? startAndEndTimeIncComparator(a, b) : b.shiftsAndGaps.length - a.shiftsAndGaps.length;

export const useDayViewGapsState = ({
	date,
	nurses,
	selectedNurse,
	selectedLocations,
	availableLocations,
}: Pick<DayViewGapsProps, "date" | "nurses" | "selectedNurse" | "selectedLocations" | "availableLocations">) => {
	const { showSnackbar } = useSnackbar();
	const { track } = useAnalytics();
	const { t } = useTranslation();
	const { chosenDepartment, timezone, hospitalData, getDayIndexByDate, getWeekIndexByDate, getNurseById, departmentNurseTypes, isHospitalView } =
		useHospitalData();
	const jobManagementEnabled = useFeatureFlag(FEATURE_FLAGS.JobManagementEnabled);
	const dayStartHour = parseTimeString(chosenDepartment.dayStart).hour;
	const { staffRequests } = useRequestsContext();
	const { recomendationFromRequests } = useRequestUtils();
	const { groupLocationsByFloorOrUnit } = useCensusGrouping(date);
	const { userInfo } = useAuth();
	const userId = userInfo?.id;
	const { selectedContractSections, handleContractSectionsChange } = useContractSectionsFilter({
		getStored: localStorageService.getDayViewGapsContractSections,
		setStored: localStorageService.setDayViewGapsContractSections,
		analyticsEvent: "dayview_filter_contract",
	});

	const [selectedRoleFilters, setSelectedRoleFilters] = useState<(NurseRole | "all")[]>(["all"]);
	const [selectedJobFilters, setSelectedJobFilters] = useState<string[]>([]);
	const [selectedShiftFilter, setSelectedShiftFilter] = useState<ShiftFilterType | ExactShiftFilterType>(ShiftFilterType.All);
	const [selectedRequestWithPending, setSelectedRequestWithPending] = useState<(OpenShiftRequest | ReplaceShiftRequest) | null>(null);
	const [selectedShiftForNurseMenu, setSelectedShiftForNurseMenu] = useState<{
		shift: NurseInShift;
		anchorEl: HTMLElement;
		quickAction?: DailyGapsQuickAction;
	} | null>(null);
	const [requestToShowBroadcastMessagePopup, setRequestToShowBroadcastMessagePopup] = useState<(OpenShiftRequest | ReplaceShiftRequest) | null>(null);
	const { weekViewData } = useWeekViewContext();
	const { customStringOrderMap } = useGlobalManualLocationOrder();
	const usePickupByLocation = useFeatureFlag(FEATURE_FLAGS.PickupByLocation);
	const [gapPostShiftState, setGapPostShiftState] = useState<GapPostShiftState | null>(null);

	// The day's canonical dimension map plus the department total, which is all the census resolver
	// needs. Note this no longer depends on the censusByLocation setting: that is a display preference
	// and must not change the numbers, which is what used to let two users of one department see
	// different gaps.
	const { byDimensions, totalCensus, demoRequired } = useMemo(() => {
		const weekIndex = getWeekIndexByDate(date);
		const dayIndex = getDayIndexByDate(date);
		const week = hospitalData.weeks[weekIndex];
		return {
			byDimensions: byDimensionsOf(week?.weeklyShifts[0]?.days[dayIndex]?.census_grouping),
			totalCensus: week === undefined ? 0 : getCensus(week, dayIndex),
			// Demo departments only. Null everywhere else, which leaves the ladder untouched.
			demoRequired: demoRequiredForDay({ department: chosenDepartment, week, dayIndex, isRealHospitalDemo: isHospitalView }),
		};
	}, [date, getWeekIndexByDate, getDayIndexByDate, hospitalData.weeks, isHospitalView, chosenDepartment]);

	const postedShiftRequests = useMemo(
		() =>
			weekViewData[date.weekdayISO].requests.filter(
				(req) =>
					req.department_id === chosenDepartment.department_id &&
					(req.request_type === NotificationType.OpenShift || req.request_type === NotificationType.ReplaceAgency) &&
					isSelectedTimeFrameValidForShiftFilter({
						selectedShiftFilter: selectedShiftFilter,
						startTime: req.message.shift_start,
						endTime: req.message.shift_end,
						allowOverlap: true,
						dayStart: chosenDepartment.dayStart,
					}),
			),
		[weekViewData, date, selectedShiftFilter, chosenDepartment.dayStart, chosenDepartment.department_id],
	);
	const replaceShiftRequests = useMemo(
		() =>
			weekViewData[date.weekdayISO].requests.filter(
				(req): req is ReplaceShiftRequest =>
					req.request_type === NotificationType.ReplaceShift &&
					isSelectedTimeFrameValidForShiftFilter({
						selectedShiftFilter: selectedShiftFilter,
						startTime: req.message.shift_start,
						endTime: req.message.shift_end,
						allowOverlap: true,
						dayStart: chosenDepartment.dayStart,
					}),
			),
		[weekViewData, date, selectedShiftFilter, chosenDepartment.dayStart],
	);
	const [sortingState, setSortingState] = useState<SortingState<RowNameAndRoles>[]>(
		localStorageService.getTableSortingState(dailyViewGapsTableSortingKey, userId) ?? defaultSortStateDailyGapView,
	);
	const sortingButtonRef = useRef<SortingButtonHandle>(null);
	const topPanelRef = useRef<HTMLDivElement>(null);

	const calculatedPostedRequests = useMemo(() => {
		const newlyCalculatedPostedRequests: CalculatedDayViewPostedRequests = {};
		for (const req of postedShiftRequests) {
			const currentRoles = req.message.roles
				.map((value) => (value === "all" ? "all" : NurseFullRole[NurseFullRoleShort[value as keyof typeof NurseFullRoleShort]] || null))
				.filter((val) => val !== null);
			// Under JobManagement, group/filter posted requests by job; otherwise by roles.
			if (jobManagementEnabled) {
				if (selectedJobFilters.length > 0 && (!req.job_id || !selectedJobFilters.includes(req.job_id))) {
					continue;
				}
			} else if (!selectedRoleFilters.includes("all") && !currentRoles.includes("all")) {
				if (!selectedRoleFilters.filter((r) => r !== "all").some((role) => currentRoles.includes(NurseFullRole[role]))) {
					continue;
				}
			}
			const rowJobId = jobManagementEnabled ? req.job_id : undefined;
			const currentKey = getDayViewGapRowKey(postedShiftString, currentRoles, rowJobId);
			if (!newlyCalculatedPostedRequests[currentKey]) {
				newlyCalculatedPostedRequests[currentKey] = {
					roles: currentRoles,
					jobId: req.job_id,
					rows: [],
				};
			}
			upsertPostedRequestIntoDayViewPostedRequests({
				row: newlyCalculatedPostedRequests[currentKey],
				start: req.message.shift_start,
				end: req.message.shift_end,
				request: req,
				date,
				startHour: dayStartHour,
			});
		}

		return newlyCalculatedPostedRequests;
	}, [postedShiftRequests, date, selectedRoleFilters, selectedJobFilters, jobManagementEnabled, dayStartHour]);

	const calculatedReplaceRequests = useMemo(() => {
		const newlyCalculatedReplaceRequests: CalculatedDayViewPostedRequests = {};
		for (const req of replaceShiftRequests) {
			const currentRoles = req.message.roles
				.map((value) => (value === "all" ? "all" : NurseFullRole[NurseFullRoleShort[value as keyof typeof NurseFullRoleShort]] || null))
				.filter((val) => val !== null);
			if (jobManagementEnabled) {
				if (selectedJobFilters.length > 0 && (!req.job_id || !selectedJobFilters.includes(req.job_id))) {
					continue;
				}
			} else if (!selectedRoleFilters.includes("all") && !currentRoles.includes("all")) {
				if (!selectedRoleFilters.filter((r) => r !== "all").some((role) => currentRoles.includes(NurseFullRole[role]))) {
					continue;
				}
			}
			const rowJobId = jobManagementEnabled ? req.job_id : undefined;
			const currentKey = getDayViewGapRowKey(replaceRequestString, currentRoles, rowJobId);
			if (!newlyCalculatedReplaceRequests[currentKey]) {
				newlyCalculatedReplaceRequests[currentKey] = {
					roles: currentRoles,
					jobId: req.job_id,
					rows: [],
				};
			}
			upsertPostedRequestIntoDayViewPostedRequests({
				row: newlyCalculatedReplaceRequests[currentKey],
				start: req.message.shift_start,
				end: req.message.shift_end,
				request: req,
				date,
				startHour: dayStartHour,
			});
		}

		return newlyCalculatedReplaceRequests;
	}, [replaceShiftRequests, date, selectedRoleFilters, selectedJobFilters, jobManagementEnabled, dayStartHour]);

	const calculatedData = useMemo(() => {
		const newlyCalculatedData: CalculatedDayViewGaps = {};
		const hasSelectedLocations = selectedLocations.length > 0;
		const normalizedSelected = selectedLocations.map((loc) => loc?.trim());
		const normalizedAvailable = availableLocations.map((loc) => loc?.trim());
		const relevantLocations = (
			hasSelectedLocations
				? chosenDepartment.locations.filter((location) => {
						const locName = location.location?.trim();
						return !!locName && (normalizedSelected.includes(locName) || !normalizedAvailable.includes(locName));
					})
				: chosenDepartment.locations
		).map<DepartmentLocation>((location) => ({
			...location,
			location: location.location?.trim(),
		}));

		const weekday = date.setLocale("en").toFormat("ccc").toLowerCase();
		const locationsWithEffectiveAmounts = relevantLocations.map<DepartmentLocation>((location) => {
			const locationCensus = resolveLocationCensus(location, byDimensions, totalCensus, chosenDepartment.statsMode, ALLOW_AVAILABILITY_FALLBACK);
			// Demo-adjusted so Day View gaps ask for the same number as Staffing.
			const amountRequired = scoreAdjustedRequiredForLocation(location, locationCensus, chosenDepartment.statsMode, weekday, { kind: "all" }, demoRequired);
			return { ...location, amountRequired };
		});
		const locationsActiveForDay = locationsWithEffectiveAmounts.filter((loc) => loc.days.includes(weekday));
		const missingLocationsSlots = findLocationsWithNoNurse(locationsActiveForDay, nurses, date, timezone, chosenDepartment.dayStart);

		for (const location of relevantLocations) {
			if (location.location) {
				const rowJobId = jobManagementEnabled ? location.jobId : undefined;
				const key = getDayViewGapRowKey(location.location, location.roles.length ? location.roles : ["all"], rowJobId);
				if (!newlyCalculatedData[key]) {
					newlyCalculatedData[key] = {
						rows: [],
						locationName: location.location,
						roles: location.roles.length ? location.roles : ["all"],
						isMissingConfig: false,
						jobId: location.jobId,
					};
				}
			}
		}

		const relevantNurses = (selectedNurse ? nurses.filter((nurseInShift) => nurseInShift.nurseId === selectedNurse.id) : nurses).map<NurseInShift>((value) => ({
			...value,
			location: value.location?.trim(),
		}));

		const nursesByTimeSort = Array.from(relevantNurses)
			.sort((a, b) => {
				return startAndEndTimeIncComparator(
					{
						start: a.startTime,
						end: a.endTime,
					},
					{
						start: b.startTime,
						end: b.endTime,
					},
				);
			})
			.filter(
				(shift) => !hasSelectedLocations || !shift.location || normalizedSelected.includes(shift.location) || !normalizedAvailable.includes(shift.location),
			)
			.filter((shift) => {
				return isSelectedTimeFrameValidForShiftFilter({
					selectedShiftFilter: selectedShiftFilter,
					startTime: shift.startTime,
					endTime: shift.endTime,
					allowOverlap: true,
					dayStart: chosenDepartment.dayStart,
				});
			})
			// Contract Type filter applies to rendered nurse rows only. Applied AFTER
			// findLocationsWithNoNurse (above) so gap blocks, HPPD totals, and census
			// totals continue to reflect the full staffing — only the row rendering
			// is filtered.
			.filter((shift) => matchesContractSections(shift.type, selectedContractSections));

		const missingSlotsByTimeSort = Array.from(missingLocationsSlots)
			.sort((a, b) => {
				return startAndEndTimeIncComparator(
					{
						start: a.start,
						end: a.end,
					},
					{
						start: b.start,
						end: b.end,
					},
				);
			})
			.filter((slot) => {
				return isSelectedTimeFrameValidForShiftFilter({
					selectedShiftFilter: selectedShiftFilter,
					startTime: slot.start,
					endTime: slot.end,
					allowOverlap: true,
					dayStart: chosenDepartment.dayStart,
				});
			})
			.map<TimeSlot>((missingLocationsSlot) => {
				const normalizedStartEnd = normalizeStartEndWithStartOfDay({
					date,
					start: missingLocationsSlot.start,
					end: missingLocationsSlot.end,
					startHour: dayStartHour,
				});
				return {
					...missingLocationsSlot,
					start: normalizedStartEnd.start,
					end: normalizedStartEnd.end,
				};
			});

		for (const nurseInShift of nursesByTimeSort) {
			if (!nurseInShift.location && !newlyCalculatedData[emptyLocationKey]) {
				newlyCalculatedData[emptyLocationKey] = {
					rows: [],
					locationName: noLocationString,
					roles: ["all"],
					isMissingConfig: false,
				};
			}
			const currentLocationString = nurseInShift.location || noLocationString;
			let relevantKey = Object.keys(newlyCalculatedData).find((key) => {
				const { locationName, roles, jobId } = getRowNameAndRolesFromKey(key);
				const locationMatch = locationName === currentLocationString;
				const roleMatch = roles.includes("all") || (nurseInShift.role && roles.includes(nurseInShift.role));
				const jobMatch = !jobManagementEnabled || !jobId || jobId === nurseInShift.jobId;
				return locationMatch && roleMatch && jobMatch;
			});
			if (relevantKey === undefined) {
				relevantKey = Object.keys(newlyCalculatedData).find((key) => {
					/**
					 * If we were unable to find a match with roles, try to match just by location (and job under JobManagement).
					 * This handles shifts wrongly assigned to a location that doesn't have its relevant roles configured.
					 */
					const { locationName, jobId } = getRowNameAndRolesFromKey(key);
					const locationMatch = locationName === currentLocationString;
					const jobMatch = !jobManagementEnabled || !jobId || jobId === nurseInShift.jobId;
					return locationMatch && jobMatch;
				});
			}
			if (relevantKey === undefined) {
				/**
				 * If we still can't find a match, create a new one with "all" roles for this location (if location is not empty)
				 * and mark it as a missing location config.
				 */
				const currentLocation = nurseInShift.location ?? "emptyLocationKey";
				const fallbackJobId = jobManagementEnabled ? nurseInShift.jobId : undefined;
				const key = getDayViewGapRowKey(currentLocation, ["all"], fallbackJobId);
				newlyCalculatedData[key] = {
					rows: [],
					locationName: currentLocation,
					roles: ["all"],
					isMissingConfig: true,
					jobId: fallbackJobId,
				};
				relevantKey = key;
			}
			const foundRow = newlyCalculatedData[relevantKey];
			if (!foundRow) {
				trackErrorEvent("day_view_gaps_location_row_not_found_appendingNurseInShift", {
					location: nurseInShift.location,
					role: nurseInShift.role,
					key: relevantKey,
				});
			} else {
				upsertSlotIntoDayViewRows({
					row: foundRow,
					start: nurseInShift.startTime,
					end: nurseInShift.endTime,
					nurse: nurseInShift,
					date,
					startHour: dayStartHour,
				});
			}
		}

		// Flag ON (pickup by location): posts carry a location, so consume each 1:1 into its location's gap
		// → `postedRequestId` → <PostedGap>. Flag OFF: posts are dept-wide; we skip the consuming matcher and
		// instead surface aggregate "(partially/fully posted X/Y)" counts (see `gapPostedCoverage`), so
		// `postedRequestId` stays undefined and no gap turns into <PostedGap>.
		const { tryMatch } = usePickupByLocation ? createPostedRequestMatcher(staffRequests) : { tryMatch: undefined };

		for (const missingSlot of missingSlotsByTimeSort) {
			if (!missingSlot.location && !newlyCalculatedData[emptyLocationKey]) {
				newlyCalculatedData[emptyLocationKey] = {
					rows: [],
					locationName: noLocationString,
					roles: ["all"],
					isMissingConfig: false,
				};
			}

			const currentLocationString = missingSlot.location || noLocationString;
			const relevantKey = Object.keys(newlyCalculatedData).find((key) => {
				const { locationName, roles, jobId } = getRowNameAndRolesFromKey(key);
				const locationMatch = locationName === currentLocationString;
				const roleMatch = roles.includes("all") || missingSlot.roles.some(/*TODO - not sure if should be every or some*/ (role) => roles.includes(role));
				const jobMatch = !jobManagementEnabled || !jobId || jobId === missingSlot.jobId;
				return locationMatch && roleMatch && jobMatch;
			});
			if (relevantKey === undefined) {
				trackErrorEvent("day_view_gaps_location_key_not_found_appendingMissingSlot", {
					location: missingSlot.location,
					roles: missingSlot.roles,
				});
			} else {
				const foundRow = newlyCalculatedData[relevantKey];
				if (!foundRow) {
					trackErrorEvent("day_view_gaps_location_row_not_found_appendingMissingSlot", {
						location: missingSlot.location,
						roles: missingSlot.roles,
						key: relevantKey,
					});
				} else {
					const startMin = missingSlot.start.hour * 60 + missingSlot.start.minute;
					const endMin = missingSlot.end.hour * 60 + missingSlot.end.minute;
					const matchResult =
						tryMatch?.(
							missingSlot.location,
							date,
							startMin,
							endMin,
							missingSlot.roles.filter((r): r is NurseFullRole => r !== "all"),
							1 /*1 required shift since each row === 1 shift*/,
							// Constrain matching to the slot's job so a different-job request at the same
							// location/time/roles can't claim this gap. Gated on jobManagementEnabled to mirror
							// the row-resolution logic above, which ignores job when the flag is off.
							jobManagementEnabled ? missingSlot.jobId : null,
						) ?? null;

					upsertSlotIntoDayViewRows({
						row: foundRow,
						start: missingSlot.start,
						end: missingSlot.end.startOf("hour"),
						missingSlot: {
							...missingSlot,
							end: roundToNearestHour(missingSlot.end),
						},
						date,
						postedRequestId: matchResult?.requestId,
						startHour: dayStartHour,
					});
				}
			}
		}

		const comparator = multiSortComparator(sortingState, customStringOrderMap);
		return Object.keys(newlyCalculatedData)
			.map((key) => getRowNameAndRolesFromKey(key))
			.sort((a, b) => {
				if (a.locationName === noLocationString) {
					return -1;
				}
				if (b.locationName === noLocationString) {
					return 1;
				}
				return comparator(a, b);
			})
			.reduce((acc, rowNameAndRoles) => {
				// Re-key with jobId so rows whose key was created with one (under JobManagement) survive the round-trip.
				const key = getDayViewGapRowKey(rowNameAndRoles.locationName, rowNameAndRoles.roles, rowNameAndRoles.jobId);
				const current = newlyCalculatedData[key];
				if (!current?.rows || current.rows.length === 0) {
					// remove empty rows
					return acc;
				}
				const { roles } = getRowNameAndRolesFromKey(key);
				if (jobManagementEnabled) {
					if (selectedJobFilters.length > 0) {
						if (rowNameAndRoles.locationName === noLocationString) {
							const filteredRows = filterNoLocationSlotsByJob(current.rows, selectedJobFilters);
							if (filteredRows.length === 0) {
								return acc;
							}
							acc[key] = { ...current, rows: filteredRows };
							acc[key].rows.sort(compareRowSlotsByDensity);
							return acc;
						}
						const rowJobId = current.jobId;
						if (!rowJobId || !selectedJobFilters.includes(rowJobId)) {
							return acc;
						}
					}
				} else if (!selectedRoleFilters.includes("all")) {
					const roleFiltersWithoutAll = selectedRoleFilters.filter((role) => role !== "all");
					if (!roles.includes("all") && !roleFiltersWithoutAll.some((role) => roles.includes(NurseFullRole[role]))) {
						return acc;
					}
				}
				acc[key] = newlyCalculatedData[key];
				acc[key].rows.sort(compareRowSlotsByDensity);
				return acc;
			}, {} as CalculatedDayViewGaps);
	}, [
		nurses,
		selectedNurse,
		chosenDepartment.locations,
		chosenDepartment.dayStart,
		chosenDepartment.statsMode,
		dayStartHour,
		date,
		timezone,
		sortingState,
		selectedShiftFilter,
		selectedRoleFilters,
		selectedJobFilters,
		jobManagementEnabled,
		availableLocations,
		selectedLocations,
		byDimensions,
		totalCensus,
		demoRequired,
		staffRequests,
		customStringOrderMap,
		selectedContractSections,
		usePickupByLocation,
	]);

	const handleShiftFilterChange = useCallback((value: ShiftFilterType | ExactShiftFilterType, _: boolean) => {
		setSelectedShiftFilter(value);
	}, []);

	const handleRoleChipChange = useCallback((roles: (NurseRole | "all")[]) => {
		setSelectedRoleFilters(roles);
	}, []);

	const handleJobChipChange = useCallback((jobIds: string[]) => {
		setSelectedJobFilters(jobIds);
	}, []);

	const handleLocationButtonClick = useCallback(
		(event: React.MouseEvent<HTMLElement>) => {
			event.stopPropagation();
			handleColumnSortClick({
				allowRemovalWhenDescendingToggle: false,
				columns: ["locationName"],
				label: "Location",
				setSortState: setSortingState,
				sortState: sortingState,
				localStorageDetails: {
					localStorageTableKey: dailyViewGapsTableSortingKey,
					userId,
				},
			});
			sortingButtonRef.current?.openMenu();
		},
		[sortingState, userId],
	);

	const handleShiftRowClick = useCallback((nurseInShift: NurseInShift, anchorEl: HTMLElement, quickAction?: DailyGapsQuickAction) => {
		trackEvent("daily_gaps_view_click_on_shift", {
			shift_id: nurseInShift.id,
			nurse_id: nurseInShift.nurseId,
			quick_action: quickAction,
		});
		setSelectedShiftForNurseMenu({
			shift: nurseInShift,
			anchorEl: anchorEl,
			quickAction: quickAction,
		});
	}, []);

	const handleCloseNurseMenu = useCallback(() => {
		setSelectedShiftForNurseMenu(null);
	}, []);

	const recommendationForSelectedRequestBroadcast = useMemo(
		() => (requestToShowBroadcastMessagePopup ? recomendationFromRequests(requestToShowBroadcastMessagePopup) : null),
		[recomendationFromRequests, requestToShowBroadcastMessagePopup],
	);
	const relevantRolesForBroadcast = useMemo(
		() => (requestToShowBroadcastMessagePopup ? requestToShowBroadcastMessagePopup.message.roles.map((r) => r as NurseFullRole) : []),
		[requestToShowBroadcastMessagePopup],
	);

	const [activeDragId, setActiveDragId] = useState<string | null>(null);
	const [activeDragData, setActiveDragData] = useState<DraggableShiftData | null>(null);
	const [optimisticMove, setOptimisticMove] = useState<OptimisticMoveState | null>(null);

	const calculatedDataWithOptimistic = useMemo(() => {
		// Optimistic data - applies optimistic move to calculatedData
		if (!optimisticMove) return calculatedData;

		// Create optimistic shift to replace the gap
		const optimisticShift: NurseInShift = {
			...optimisticMove.sourceNurseInShift,
			location: optimisticMove.targetLocationName,
			startTime: optimisticMove.targetStart,
			endTime: optimisticMove.targetEnd,
		};

		// Deep clone calculatedData to avoid mutations
		const clonedData: CalculatedDayViewGaps = {};
		// Track if we replaced the gap (only replace once)
		let didReplaceGap = false;

		for (const key of Object.keys(calculatedData)) {
			const row = calculatedData[key];
			const isTargetLocation = key === optimisticMove.targetLocationKey;

			clonedData[key] = {
				...row,
				rows: row.rows.map((slot) => ({
					...slot,
					shiftsAndGaps: slot.shiftsAndGaps.map((item) => {
						// Mark source shift as being moved
						if (item.specificType === "shift" && item.value.id === optimisticMove.sourceShiftId && item.value.nurseId === optimisticMove.sourceNurseId) {
							return {
								...item,
								value: { ...item.value, isBeingMoved: true },
							};
						}

						// Replace the matching gap with the optimistic shift (in-place)
						if (isTargetLocation && item.specificType === "gap" && !didReplaceGap) {
							const gapStart = item.value.timeSlot.start;
							const gapEnd = item.value.timeSlot.end.startOf("hour");
							if (gapStart.equals(optimisticMove.targetStart) && gapEnd.equals(optimisticMove.targetEnd)) {
								didReplaceGap = true;
								return {
									specificType: "shift" as const,
									value: { ...optimisticShift, isOptimisticLoading: true },
									normalizedStartTime: item.normalizedStartTime,
									normalizedEndTime: item.normalizedEndTime,
								};
							}
						}

						return item;
					}),
				})),
			};
		}

		return clonedData;
	}, [calculatedData, optimisticMove]);

	const gapsLocationFilters = useMemo<LocationFilters | undefined>(() => {
		const hasRoleFilter = !selectedRoleFilters.includes("all");
		const hasTimeFilter = typeof selectedShiftFilter === "object";
		if (!hasRoleFilter && !hasTimeFilter) return undefined;
		return {
			roles: hasRoleFilter ? selectedRoleFilters.filter((r): r is NurseRole => r !== "all").map((r) => rolesMap[r]) : undefined,
			timeRange: hasTimeFilter ? { start: selectedShiftFilter.start, end: selectedShiftFilter.end } : undefined,
		};
	}, [selectedRoleFilters, selectedShiftFilter]);

	const groupedLocationsWithOptimistic = useMemo(
		() => groupLocationsByFloorOrUnit(calculatedDataWithOptimistic, nurses, gapsLocationFilters),
		[calculatedDataWithOptimistic, nurses, groupLocationsByFloorOrUnit, gapsLocationFilters],
	);

	// A group holds the picked person when one of its location rows still renders a shift. Derived from
	// `calculatedData` on purpose: `groupLocationsByFloorOrUnit` is handed the UNFILTERED nurses, so
	// `group.nursesCount` is not a valid signal here. Empty while nobody is picked, so every group goes
	// back to however the user left it.
	const autoExpandedGroups = useMemo<ReadonlySet<string>>(() => {
		if (!selectedNurse || !groupedLocationsWithOptimistic?.length) return new Set();
		const groupsWithMatch = groupedLocationsWithOptimistic.filter((group) =>
			group.locationKeys.some((locationKey) =>
				calculatedDataWithOptimistic[locationKey]?.rows.some((slot) => slot.shiftsAndGaps.some((item) => item.specificType === "shift")),
			),
		);
		return new Set(groupsWithMatch.map((group) => group.groupKey));
	}, [selectedNurse, groupedLocationsWithOptimistic, calculatedDataWithOptimistic]);

	const { expandedGroups, toggleGroupExpand } = useExpandedGroups({ autoExpandedGroups });

	// The posted requests rendered in the top "Posted Shift" section — used as the coverage source.
	const postedRequestsForCoverage = useMemo(
		() =>
			Object.values(calculatedPostedRequests).flatMap((row) =>
				row.rows.flatMap((slot) => slot.requests.map((r) => r.value).filter((req) => req.request_type === NotificationType.OpenShift)),
			),
		[calculatedPostedRequests],
	);

	// Aggregate posted-shift coverage per gap signature — only when pickup_by_location is OFF (ON keeps the
	// consuming matcher path). Built from the rendered gaps + posted shifts so keys align with the render sites.
	const gapPostedCoverage = useMemo(() => {
		if (usePickupByLocation) return null;
		const gapTimeSlots: TimeSlot[] = [];
		for (const row of Object.values(calculatedDataWithOptimistic)) {
			for (const rowSlot of row.rows) {
				for (const item of rowSlot.shiftsAndGaps) {
					if (item.specificType === "gap") gapTimeSlots.push(item.value.timeSlot);
				}
			}
		}
		return computeGapPostedCoverageBySignature({ gapTimeSlots, postedRequests: postedRequestsForCoverage, jobManagementEnabled });
	}, [usePickupByLocation, calculatedDataWithOptimistic, postedRequestsForCoverage, jobManagementEnabled]);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8, // Require 8px movement before starting drag
			},
		}),
	);

	const { updateLocationAndHours } = useUpdateLocationAndHoursForShift();

	const handleDragStart = useCallback((event: DragStartEvent) => {
		setActiveDragId(event.active.id?.toString() ?? null);
		setActiveDragData(event.active.data.current as DraggableShiftData);
	}, []);

	const handleDragEnd = useCallback(
		async (event: DragEndEvent) => {
			const { active, over } = event;
			setActiveDragId(null);
			setActiveDragData(null);

			if (active && over) {
				const sourceData = active.data.current as DraggableShiftData;
				const targetData = over.data.current as DroppableGapData;
				if (sourceData && targetData) {
					// Anchor gap times to the correct calendar date for this staffing day. Gap DateTimes from
					// overnight location configs can land on the wrong calendar day (e.g. 4/1 00:00 instead
					// of 3/31 00:00) even though they display correctly.
					const { start: correctedStart, end: correctedEnd } = adjustNightShiftRange(targetData.gapStart, targetData.gapEnd, date, chosenDepartment.dayStart);

					if (
						sourceData.nurseInShift.location === targetData.locationName &&
						sourceData.nurseInShift.startTime.diff(correctedStart, "hours").hours === 0 &&
						sourceData.nurseInShift.endTime.diff(correctedEnd, "hours").hours === 0
					) {
						showSnackbar(`${sourceData.nurseInShift.name} is already scheduled at ${targetData.locationName} for the selected time.`, "info");
						return;
					}

					// Under JobManagement, the destination location's job must be one the dragged nurse can work.
					// On success, the shift's job assignment is updated to the destination's job.
					let updatedJobId: string | undefined = sourceData.nurseInShift.jobId;
					let updatedJobName: string | undefined = sourceData.nurseInShift.jobName;
					if (jobManagementEnabled) {
						const targetJobId = calculatedData[targetData.locationKey]?.jobId;
						if (targetJobId) {
							const draggedNurse = getNurseById(sourceData.nurseInShift.nurseId);
							const nurseCanWorkJob = draggedNurse?.jobIds?.includes(targetJobId) ?? false;
							const targetJob = chosenDepartment.jobs?.find((j) => j.id === targetJobId);
							if (!nurseCanWorkJob) {
								showSnackbar(
									t("dnd.nurse.missing.job", {
										nurseName: sourceData.nurseInShift.name,
										jobName: targetJob?.displayName ?? "this job",
									}),
									"warning",
								);
								return;
							}
							updatedJobId = targetJobId;
							updatedJobName = targetJob?.displayName;
						}
					}

					// Use raw gap times for optimistic state so the gap-matching comparison works
					setOptimisticMove({
						sourceShiftId: sourceData.shiftId,
						sourceNurseId: sourceData.nurseId,
						sourceNurseInShift: sourceData.nurseInShift,
						targetLocationKey: targetData.locationKey,
						targetLocationName: targetData.locationName,
						targetStart: targetData.gapStart,
						targetEnd: targetData.gapEnd,
					});

					try {
						if (
							await updateLocationAndHours({
								originalNurseInShift: sourceData.nurseInShift,
								updatedNurseInShift: {
									...sourceData.nurseInShift,
									location: targetData.locationName,
									startTime: correctedStart,
									endTime: correctedEnd,
									jobId: updatedJobId,
									jobName: updatedJobName,
								},
							})
						) {
							showSnackbar(`${sourceData.nurseInShift.name} moved to ${targetData.locationName}`, "success");
						} else {
							showSnackbar(`No changes made to ${sourceData.nurseInShift.name}'s shift.`, "info");
						}
					} catch {
						//handled inside updateLocationAndHours
					} finally {
						// Clear optimistic state after API call completes
						setOptimisticMove(null);
					}
				}
			}
		},
		[showSnackbar, updateLocationAndHours, date, chosenDepartment.dayStart, chosenDepartment.jobs, calculatedData, getNurseById, jobManagementEnabled, t],
	);

	const isDraggingInContext = activeDragId !== null;

	const handleGapClick = useCallback(
		(locationName: string, gapStart: DateTime, gapEnd: DateTime, roles: (NurseFullRole | "all")[], jobId: string | null) => {
			const { start: correctedStart, end: correctedEnd } = adjustNightShiftRange(gapStart, gapEnd, date, chosenDepartment.dayStart);
			const correctedLocationName = usePickupByLocation ? locationName : "";
			const resolvedRoles: NurseFullRole[] = roles.includes("all") ? departmentNurseTypes : (roles as NurseFullRole[]);

			track("daily_gaps_view_missing_click_post_shift", {
				location: correctedLocationName,
				gap_start: correctedStart.toISO(),
				gap_end: correctedEnd.toISO(),
				roles: JSON.stringify(resolvedRoles),
			});
			setGapPostShiftState({
				locationName: correctedLocationName,
				startTime: correctedStart,
				endTime: correctedEnd,
				roles: resolvedRoles,
				dayNo: date.weekdayISO,
				jobId: jobManagementEnabled && jobId ? jobId : null,
			});
		},
		[date, track, chosenDepartment.dayStart, usePickupByLocation, jobManagementEnabled, departmentNurseTypes],
	);

	return {
		calculatedData: calculatedDataWithOptimistic,
		calculatedPostedRequests,
		calculatedReplaceRequests,
		selectedRoleFilters,
		selectedJobFilters,
		selectedShiftFilter,
		sortingState,
		setSortingState,
		sortingButtonRef,
		topPanelRef,
		handleShiftFilterChange,
		handleRoleChipChange,
		handleJobChipChange,
		handleLocationButtonClick,
		setSelectedRequestWithPending,
		selectedRequestWithPending,
		handleShiftRowClick,
		selectedShiftForNurseMenu,
		handleCloseNurseMenu,
		setRequestToShowBroadcastMessagePopup,
		requestToShowBroadcastMessagePopup,
		relevantRolesForBroadcast,
		recommendationForSelectedRequestBroadcast,
		groupedLocations: groupedLocationsWithOptimistic,
		toggleGroupExpand,
		expandedGroups,
		isDraggingInContext,
		activeDragId,
		activeDragData,
		sensors,
		handleDragStart,
		handleDragEnd,
		dayStartHour,
		usePickupByLocation,
		handleGapClick,
		gapPostShiftState,
		setGapPostShiftState,
		gapPostedCoverage,
		selectedContractSections,
		handleContractSectionsChange,
	};
};
